import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Award,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  FileText,
  Folder,
  LayoutList,
  Lightbulb,
  Loader2,
  MessageSquare,
  Minus,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Trophy,
  UserCheck,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import RefreshFailedNotice from '../../components/RefreshFailedNotice'
import { formatDate, formatDateTime, formatNumber } from '../../lib/format'
import {
  adminOverviewResponse,
  aiUsageResponse,
  filesStorageSummary,
} from '../../lib/types'
import {
  greetingName,
  timeOfDay,
  formatBytes,
  taskHealthPartition,
} from '../../lib/dashboard-helpers'
import { useAuthStore } from '../../stores/authStore'
import { Avatar } from '../../components/Avatar'
import { DonutChart, Legend, RankedBarChart, TrendChart } from '../../components/charts/Charts'
import { TaskCompletionPie } from '../../components/charts/TaskCompletionPie'
import { DONUT_PALETTE } from '../../components/charts/palette'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'

// Pure formatters were extracted to `lib/dashboard-helpers.js` so they
// can be unit-tested without a JSX/DOM toolchain. The imports at the
// top of this file pull them back in unchanged.

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

// ---------- KPI tile ----------

// Per-tone colour variants for the KPI icon container. Restored
// 2026-05-22 — previously every tile painted its icon in muted grey
// (the "all-grey" pass for a calmer dashboard), now reintroduced so
// the icon strip reads as a row of distinct chips that the eye can
// scan at a glance.
//
// Tone palette is light-tinted background + vivid mid-tone glyph,
// each pair picked from the same family as the tone name (gold =
// amber, sky = blue, emerald = green, …). Border colour matches the
// glyph at 20%-ish opacity so the chip has a subtle outline of its
// own family rather than the neutral page rule.
//
// `halo` is the soft blurred gradient that bleeds out behind the
// chip — uses the chip's tinted background hex so the halo reads
// as the same family as the icon.
const TONE_CLASSES = {
  // Dark mode: a SOFT per-tone wash (15% tint + tinted glyph + faint tone
  // border) instead of the earlier uniform white/6% neutral tile. The
  // neutral tile only LOOKED right behind circle-shaped glyphs
  // (CheckCircle2 / AlertCircle read as coloured badges on their own);
  // linear glyphs (ClipboardList, UserCheck) sat nearly invisible on it,
  // so the hero strip's four icons looked inconsistent (QA-reported).
  // 15% keeps the tiles far quieter than light mode's solid pastels —
  // the original "no bright blocks on the dark card" intent survives.
  gold: {
    tile:
      'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
    halo: 'from-[#FEF3C7] to-transparent dark:from-transparent',
  },
  sky: {
    tile:
      'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile:
      'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile:
      'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400/20',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  rose: {
    tile:
      'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  violet: {
    tile:
      'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  pink: {
    tile:
      'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-400/20',
    halo: 'from-[#FCE7F3] to-transparent dark:from-transparent',
  },
  // Neutral fallback — kept for any callsite that hasn't been
  // assigned a colourful tone yet.
  slate: {
    tile:
      'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
    halo: 'from-[color:var(--color-canvas)] to-transparent',
  },
}

// `KpiTile` (the small-card variant) was removed in the May-2026 dashboard
// sweep — the hero strip uses `KpiHeroTile` (defined below) for every
// surface, so the smaller component had no remaining callers.

// ---------- KPI hero tile (premium 4-tile strip) ----------

// `TONE_STROKE` removed along with the in-card sparkline — was used
// only to colour the per-tile trend curve. `LeaderboardSparkline`
// uses its own gold stroke and doesn't reference this map.

/** Smooth catmull-rom path through a series of `[x,y]` points. Output
 *  is an SVG path `d` attribute ready for a `<path>` element.
 *
 *  We use Catmull-Rom (converted to cubic-Bezier) rather than a raw
 *  polyline because the resulting curve passes through every data
 *  point AND damps the high-frequency jaggedness a 14-day series
 *  often produces — adjacent days of 0 / 7 / 0 / 9 / 0 look like a
 *  sawtooth as a polyline, like a wave as a curve. The tension
 *  factor of 0.5 is the canonical Catmull-Rom value. */
function smoothPath(points) {
  if (points.length === 0) return ''
  if (points.length === 1) return `M${points[0][0]} ${points[0][1]}`
  const d = [`M${points[0][0]} ${points[0][1]}`]
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? p2
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6
    d.push(`C${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`)
  }
  return d.join(' ')
}

// `Sparkline` (the in-card 14-day trend curve) was removed across
// all KPI cards — the soft wave at the bottom of each tile competed
// visually with the headline number. The `smoothPath` helper above
// is retained because `LeaderboardSparkline` further down the file
// still uses it for the inline per-row gold curve next to each
// leaderboard entry.

// `DeltaChip` removed — the percentage delta below each KPI value
// ("+100% · new tasks vs last week", "No change · newly overdue
// vs last week", etc.) read as misleading noise on freshly-seeded
// tenants where every change against a zero baseline computes to
// "+100%" or "No change". Hero tiles now show the headline figure
// alone with a static muted hint underneath where helpful.

/** Hero KPI tile — bigger than `KpiTile`. Shows label + headline
 *  number + optional muted hint + optional "View →" link. Shares the
 *  tone palette + halo behaviour of the base tile so the row reads
 *  as one family.
 *
 *  Earlier versions of this tile carried a 14-day sparkline behind
 *  the value AND a delta chip ("+100% vs last week") below it. Both
 *  were removed — the sparkline competed visually with the headline
 *  number, and the delta chips on a freshly-seeded tenant always
 *  read as "+100% / No change" relative to a zero baseline which is
 *  noise, not signal. The tile now carries the figure alone. */
function KpiHeroTile({
  label,
  value,
  icon: Icon,
  tone,
  to,
  hint,
}) {
  const tones = TONE_CLASSES[tone]
  const body = (
    <>
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* `min-h-[2.5rem]` reserves vertical space for a 2-line
           *  label so the value sits at the same y-coordinate across
           *  every tile in the row — even when one tile's label
           *  wraps (e.g. "Active Members · 14 Days") and the others
           *  don't. Mirrors the Manager / Team Leader / Employee
           *  KpiHeroTile so all four panels share the same rhythm. */}
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 min-h-[2.5rem] leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number'
              ? formatNumber(value)
              : value}
          </p>
          {/* Secondary line — muted hint string (e.g. "Across every
           *  workspace"). The delta-chip slot that used to share
           *  this row has been removed; the tile now shows the
           *  headline number alone, with the hint as plain context
           *  underneath when present. */}
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)] m-0">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
      {/* "View →" pinned to the card's bottom edge via `mt-auto` so
       *  the four hero tiles in this row align their View links on
       *  one horizontal line regardless of label wrap. Mirrors the
       *  Manager / Team Leader / Employee strip's KpiHeroTile so all
       *  four user panels share the same View-link rhythm.
       *
       *  History: earlier this used `mt-2` to keep the link visually
       *  tight under the value. The moment any tile's label wrapped
       *  to two lines (e.g. "Reports Submitted · This Week") the
       *  View links lost their alignment. Bottom-anchored Views
       *  trade a sliver of dead whitespace on the shorter tiles for
       *  consistent horizontal alignment across the whole row. */}
      {to ? (
        <div className="relative mt-auto pt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] opacity-80 group-hover:opacity-100">
          View
          <ArrowRight
            size={12}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </div>
      ) : null}
    </>
  )

  // Plain `p-5` only — the previous `pb-14` reserved bottom space
  // for the absolutely-positioned Sparkline. With the sparkline
  // removed, tiles now hug their content for a tighter feel. CSS
  // Grid still auto-stretches the four siblings to the tallest
  // natural height, so the row stays horizontally aligned.
  return to ? (
    <Link
      to={to}
      className="group flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
    >
      {body}
    </Link>
  ) : (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      {body}
    </div>
  )
}

// ---------- Compliance ring (CSS-only conic gradient) ----------

function ComplianceRing({
  rate,
  label,
  submitted,
  eligible,
  tone,
}) {
  const { ref, hover, track, clear } = useChartHover()
  // `rate === null` means there are no report-eligible members in this
  // tenant — render an explicit empty state rather than a fake 0 %
  // ring that reads as "team is failing compliance" when really there's
  // nobody on the eligibility roster.
  const noEligible = rate === null || eligible === 0
  const safeRate = noEligible ? 0 : Math.max(0, Math.min(100, rate))
  const accentColor =
    tone === 'emerald'
      ? 'var(--color-success)'
      : tone === 'amber'
        ? 'var(--color-warning)'
        : tone === 'sky'
          ? 'var(--color-info)'
          : 'var(--color-accent-strong)'
  // Single-value hover payload — the ring label plus a "submitted /
  // eligible · rate%" value line. Suppressed when there's no eligible
  // roster (the ring renders a muted em-dash empty state instead).
  const ringHover = noEligible
    ? undefined
    : {
        color: accentColor,
        label,
        value: `${formatNumber(submitted)}/${formatNumber(eligible)} · ${safeRate}%`,
      }
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 flex items-center gap-4">
      <div
        ref={ref}
        className="h-20 w-20 rounded-full grid place-items-center shrink-0 relative"
        style={{
          background: noEligible
            ? 'var(--color-canvas)'
            : `conic-gradient(${accentColor} 0% ${safeRate}%, var(--color-canvas) ${safeRate}% 100%)`,
        }}
        onMouseEnter={ringHover ? track(ringHover) : undefined}
        onMouseMove={ringHover ? track(ringHover) : undefined}
        onMouseLeave={ringHover ? clear : undefined}
      >
        <div className="h-14 w-14 rounded-full bg-[color:var(--color-card)] border border-[color:var(--color-rule)] grid place-items-center">
          <span
            className="font-display text-base text-[color:var(--color-ink-strong)] tabular-nums"
            style={{ color: noEligible ? 'var(--color-ink-muted)' : accentColor }}
          >
            {noEligible ? '—' : `${safeRate}%`}
          </span>
        </div>
        {hover ? <ChartTooltip {...hover} /> : null}
      </div>
      <div className="min-w-0">
        {/* Universal card-heading class — same scale as every other
         *  card title on the dashboard. */}
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {label}
        </h3>
        {noEligible ? (
          <>
            <p className="mt-1 text-sm text-[color:var(--color-ink-strong)] font-normal">
              No eligible members
            </p>
            <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
              Add teammates with role <em>User</em> to enable reporting
            </p>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-[color:var(--color-ink-strong)] font-normal">
              {formatNumber(submitted)}{' '}
              <span className="text-[color:var(--color-ink-muted)] font-normal">
                of {formatNumber(eligible)} submitted
              </span>
            </p>
            <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
              {safeRate >= 80
                ? 'Healthy'
                : safeRate >= 50
                  ? 'Needs reminders'
                  : 'Reach out to the team'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

// ---------- Bar chart ----------

function BarRows({
  rows,
  formatter = (n) => formatNumber(n),
  emptyLabel,
}) {
  const { ref, hover, track, clear } = useChartHover()
  if (rows.length === 0) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-4 text-center italic">
        {emptyLabel}
      </p>
    )
  }
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <div ref={ref} className="group relative space-y-3">
      {rows.map((r) => {
        // Force an empty bar (0%) when the value is zero so the row
        // reads as "no data" rather than showing a 2% sliver. Tiny-
        // but-non-zero rows still get the 2% floor for visibility.
        const pct = r.value > 0 ? Math.max(2, (r.value / max) * 100) : 0
        const payload = {
          color: 'var(--color-accent-strong)',
          label: r.name,
          value: formatter(r.value),
        }
        return (
          <div
            key={r.name}
            className="transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100"
            onMouseEnter={track(payload)}
            onMouseMove={track(payload)}
            onMouseLeave={clear}
          >
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                {r.name}
              </p>
              <p className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums">
                {formatter(r.value)}
              </p>
            </div>
            <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[color:var(--color-accent-strong)] to-[color:var(--color-accent)]"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )
      })}
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

// ---------- Workload by Department (removed 2026-05-22) ----------
// The squarified treemap + its supporting `squarify()` algorithm
// + `UNASSIGNED_DEPT_KEY` constant were removed end-to-end. The
// section produced low signal on tenants where Spaces hadn't been
// routed to Departments; admins use the Task Monitor (`/task-monitor`)
// for the per-department drill-down instead. Backend aggregation and
// the `workloadByDepartment` schema field were stripped in the same
// pass. Git history holds the prior treemap if it's ever brought back.

// ---------- At-Risk Tasks ----------

/** Single row in the at-risk triage list. */
function AtRiskRow({ task }) {
  const reasonChip = (() => {
    if (task.riskReason === 'overdue') {
      const days = Math.max(1, Math.round(task.riskHours / 24))
      return {
        tone: 'rose',
        label: `Overdue · ${days} ${days === 1 ? 'day' : 'days'}`,
      }
    }
    if (task.riskReason === 'due_today') {
      return { tone: 'amber', label: 'Due today' }
    }
    const days = Math.max(1, Math.round(task.riskHours / 24))
    return {
      tone: 'sky',
      label: `Idle · ${days} ${days === 1 ? 'day' : 'days'}`,
    }
  })()
  const chipClass = TONE_CLASSES[reasonChip.tone].tile
  // Open THIS task: the Space list route with the `?task=` deep-link,
  // which SpacePage resolves into the task's detail panel. The route
  // lives in the SHARED `/app/*` group (RoleAwareLayout, allowRoles
  // user + company_admin + sub_admin), so admins land there fine — an
  // older comment here claimed `/app/*` was user-only and swapped the
  // link for a generic filtered Task Monitor URL, which made every
  // row's "Open" go to the same page (QA-reported). The feed already
  // ships spaceId + listId per row. Filtered-Monitor fallback kept
  // for defensive rendering when spaceId is null (schema allows it).
  let openHref
  if (task.spaceId && task.listId) {
    openHref = `/app/spaces/${task.spaceId}/lists/${task.listId}?task=${task.id}`
  } else {
    const todayIso = new Date().toISOString().slice(0, 10)
    const yesterdayIso = new Date(new Date().getTime() - 86_400_000)
      .toISOString()
      .slice(0, 10)
    openHref = '/task-monitor'
    if (task.riskReason === 'overdue') {
      openHref = `/task-monitor?dueTo=${yesterdayIso}`
    } else if (task.riskReason === 'due_today') {
      openHref = `/task-monitor?dueFrom=${todayIso}&dueTo=${todayIso}`
    }
  }
  const taskName =
    task.name.length > 60 ? task.name.slice(0, 59) + '…' : task.name
  const dueLabel = task.dueDate ? `Due ${formatDateTime(task.dueDate)}` : null
  return (
    <div className="flex items-center gap-3 py-3">
      {task.assigneeName ? (
        <Avatar
          name={task.assigneeName}
          avatarUrl={task.assigneeAvatarUrl}
          size="sm"
          colorByName
        />
      ) : (
        <span
          aria-label="Unassigned"
          className="inline-flex h-7 w-7 rounded-full border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] items-center justify-center text-[11px] shrink-0"
        >
          —
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
          {taskName}
        </p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span
            className={
              'inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ' +
              chipClass
            }
          >
            {reasonChip.label}
          </span>
          {task.assigneeName ? (
            <span className="text-[12px] text-[color:var(--color-ink-muted)] truncate">
              {task.assigneeName}
            </span>
          ) : (
            <span className="text-[12px] text-[color:var(--color-ink-muted)] italic">
              Unassigned
            </span>
          )}
          {dueLabel ? (
            <span className="text-[12px] text-[color:var(--color-ink-muted)]">
              · {dueLabel}
            </span>
          ) : null}
        </div>
      </div>
      <Link
        to={openHref}
        className="shrink-0 inline-flex items-center gap-1 text-[13px] font-medium text-[color:var(--color-accent-strong)] hover:underline"
        aria-label={`Open task ${taskName}`}
      >
        Open
        <ExternalLink size={12} aria-hidden="true" />
      </Link>
    </div>
  )
}

// `QuickAction` (singular) was removed alongside the bottom-of-page
// "Quick actions" section — that section duplicated the new 4-step
// onboarding tile in the Analytics row, which is more functional
// (numbered checklist) and more visible (above the fold). The
// vertical-step component `QuickActions` (plural) lives further
// down near the donut/legend helpers.

// ---------- Page ----------

// ---------- AI Insights strip (removed 2026-05-22) ----------
// The "What Changed" narrative-cards strip was removed at the Company
// Admin's request — the strip read as noise on quiet tenants and the
// signal it carried is already surfaced elsewhere (At-Risk Tasks card,
// hero delta chips, Report Compliance rings). The corresponding
// backend service, route, and Zod schema were deleted in the same
// pass. If insights are ever brought back, the prior implementation
// lives in git history.

// ---------- Bottleneck Sankey (removed 2026-05-22) ----------
// The status × department Sankey + table + card were removed at the
// Company Admin's request. The status / department signals it carried
// are still available on the Task Monitor page (`/task-monitor`); the
// dashboard now jumps straight from the Workload treemap to the Action
// Needed triage row. The backend `bottleneck` aggregation in
// admin.service.js was stripped in the same pass. Git history holds
// the prior Sankey implementation if it's ever brought back.

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)

  const overviewQuery = useQuery({
    queryKey: ['admin-overview'],
    queryFn: async () => {
      const res = await api.get('/admin/overview')
      // The overview payload is a large, deeply-nested analytics blob.
      // A strict `.parse()` here meant a SINGLE drifting field anywhere
      // in the tree (e.g. a stored report-schedule whose
      // `dailyActiveDays` the dashboard endpoint didn't back-fill, or a
      // legacy id that fails Zod's strict uuid check) threw a ZodError —
      // which surfaced to the user as the blanket "Something went wrong.
      // Please try again." on an otherwise-healthy dashboard.
      //
      // Switch to `safeParse`: genuine transport errors (network / 5xx)
      // still reject above this line via axios and drive the friendly
      // error + retry UI. A pure schema mismatch no longer blanks the
      // page — we log the exact offending field paths for diagnosis and
      // render from the raw payload instead (every card below already
      // guards its inputs with `?? []` / optional chaining). The
      // dashboard keeps loading with all available data while contract
      // drift stays visible to engineers in any environment.
      const parsed = adminOverviewResponse.safeParse(res.data)
      if (parsed.success) return parsed.data
      const paths = parsed.error.issues
        .map((i) => i.path.join('.') || '(root)')
        .join(', ')
      console.warn(
        '[dashboard] /admin/overview response did not match the expected ' +
          'schema; rendering from the raw payload. Drifting field(s): ' +
          paths,
      )
      return res.data
    },
    refetchInterval: 60_000,
  })

  // Storage summary — same query key as FilesPage so the cache is
  // shared and only one round-trip happens per session. Long staleTime
  // because file totals don't move much on a dashboard refresh cadence.
  const storageQuery = useQuery({
    queryKey: ['files', 'storage'],
    queryFn: async () => {
      const res = await api.get('/files/storage')
      return filesStorageSummary.parse(res.data)
    },
    staleTime: 5 * 60_000,
  })

  // AI usage — same query key as AiUsagePage so the cache is shared
  // and the dashboard's third Analytics card hydrates instantly when
  // the operator navigates back from `/ai-usage`. Refetches on the
  // same 60s cadence as the rest of the dashboard so the progress
  // bar stays roughly live.
  const aiUsageQuery = useQuery({
    queryKey: ['ai-usage'],
    queryFn: async () => {
      const res = await api.get('/ai-usage')
      return aiUsageResponse.parse(res.data)
    },
    refetchInterval: 60_000,
  })
  // The AI usage call fails two ways: the plan doesn't include the ai_usage
  // feature (requireFeature → 403 `feature_not_in_plan`, on EVERY load for
  // such plans) or a genuine error. Both used to leave the card on
  // "Loading AI usage…" forever — the same bug QA fixed on StorageDonut.
  const aiUsageNotInPlan =
    aiUsageQuery.isError &&
    aiUsageQuery.error?.response?.status === 403 &&
    aiUsageQuery.error?.response?.data?.error?.code === 'feature_not_in_plan'
  const aiUsageError =
    aiUsageQuery.isError && !aiUsageNotInPlan
      ? extractApiError(aiUsageQuery.error).message
      : null
  // Storage: a 403/404 is the by-design access denial (e.g. an external
  // collaborator) → friendly "not available" copy; anything else (5xx,
  // network) is a real failure and shows its message instead of
  // masquerading as a permission limit.
  const storageStatus = storageQuery.error?.response?.status
  const storageErrorMessage =
    storageQuery.isError && storageStatus !== 403 && storageStatus !== 404
      ? extractApiError(storageQuery.error).message
      : null

  return (
    <div>
      {/* No breadcrumb — root page; see HomePage for the rationale. */}

      {/* Flat hero — heading + role badge + subtitle. The live date/time
       *  now lives in the header clock pill, so the hero no longer
       *  repeats a static date. */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-medium text-[color:var(--color-ink-strong)]">
              Good {timeOfDay()}, {greetingName(user?.fullName)}
            </h1>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-normal border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] shrink-0">
              {/* This dashboard serves BOTH admin roles — the badge was
               *  hardcoded "Company Admin", so Sub Admins saw the wrong
               *  role next to their own greeting while the header pill
               *  showed the right one (QA-reported). */}
              {user?.role === 'sub_admin' ? 'Sub Admin' : 'Company Admin'}
            </span>
          </div>
          <p className="mt-1 text-[color:var(--color-ink-muted)]">
            What's happening at{' '}
            {tenant?.name ?? 'your company'}.
          </p>
        </div>
      </div>

      {/* [QA-14] Cached data first: a failed background refetch keeps the
       *  last good numbers on screen with a non-blocking notice; the
       *  blocking error is only for a load that never produced data. */}
      {overviewQuery.data ? (
        <>
          {overviewQuery.isError ? (
            <RefreshFailedNotice
              error={overviewQuery.error}
              onRetry={() => overviewQuery.refetch()}
            />
          ) : null}
          <DashboardBody
            data={overviewQuery.data}
            storage={storageQuery.data}
            storageError={storageQuery.isError}
            storageErrorMessage={storageErrorMessage}
            aiUsage={aiUsageQuery.data}
            aiUsageNotInPlan={aiUsageNotInPlan}
            aiUsageError={aiUsageError}
          />
        </>
      ) : overviewQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading dashboard…
        </p>
      ) : overviewQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(overviewQuery.error).message}
        </p>
      ) : null}
    </div>
  )
}

// `TopPerformerTile` was removed when the top-performer story moved
// from the KPI strip (4th tile) to its own AnalyticsCard alongside
// Quick Actions. The new render lives in `TopPerformerBody` further
// down, used inside the `<AnalyticsCard title="Top Performers">` in
// the Analytics section.

/** Section heading with optional helper text + right-side action.
 *  Drives consistent visual rhythm between dashboard sections.
 *
 *  Uses the page's display font at a true heading size so each section
 *  reads as a proper chapter divider — bigger than a label, smaller
 *  than the page H1. */
function SectionHeader({
  title,
  hint,
  action,
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 mt-10 mb-4">
      <div>
        {/* Section heading — `text-lg` reads as a proper chapter divider
         *  above the cards while staying refined. Same scale on every
         *  dashboard (Master Admin, Sub Admin, User). */}
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h2>
        {hint ? (
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {hint}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

// ---------- Analytics card frame + donut compositions ----------

/** Heading for one part of a combined card (e.g. "Storage by File Type"
 *  and "AI Usage" sharing the Storage & AI Usage box) — one notch below
 *  the card title, with its figure on the right. */
function SubCardHeading({ title, meta }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h4 className="m-0 text-sm font-semibold text-[color:var(--color-ink-strong)]">{title}</h4>
      {meta ? (
        <p className="m-0 shrink-0 text-[13px] text-[color:var(--color-ink-muted)] tabular-nums">{meta}</p>
      ) : null}
    </div>
  )
}

/** Generic frame around an analytics widget — same look on every
 *  card in the analytics row so the grid reads as one block. */
function AnalyticsCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 min-h-[220px]">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        {/* Card heading at text-lg (16-17px responsive) — sits one
         *  notch below the page Section Header so the visual
         *  hierarchy reads Page → Section → Card. */}
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

// `TaskApprovalDonut` was removed alongside its analytics card —
// task approval isn't a tenant-wide feature so the donut's "No
// flow" bucket dominated and the chart added no signal. The
// component can be restored from git history if a future
// `task_approvals` plan-feature flag wants it back.

// `RoleDonut` ("Members by Work Role") was removed from the Company Admin
// dashboard on request (2026-10-06). The `members.byWorkRole` payload still
// feeds the Sub Admin dashboard's donut; restore from git history if wanted.

/** Donut + legend for tenant storage split across the four MIME-type
 *  buckets the backend classifies. Empty state renders a muted "no
 *  files yet" message rather than a sliver of nothing. */
function StorageDonut({ storage, error, errorMessage }) {
  // Error before the loading branch — a failed request leaves `storage`
  // undefined forever, and rendering the loading copy for it spun an
  // endless "Loading storage…" (QA-reported). Only when there's no cached
  // data, though: a failed background refetch keeps showing good numbers.
  // A 403/404 is the by-design denial (the files API default-denies
  // external collaborators on tenant-wide surfaces), so it gets a graceful
  // "not available" note; any other failure shows its real message.
  if (error && !storage) {
    if (errorMessage) {
      return (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          {errorMessage}
        </p>
      )
    }
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        Storage details aren't available for your account.
      </p>
    )
  }
  if (!storage) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        Loading storage…
      </p>
    )
  }
  const { breakdown } = storage
  // Only Documents + Images are now uploadable (audio / video /
  // archives are rejected at the API layer by the allowlist in
  // `files.service.assertAllowedUploadType`). The two retired buckets
  // are folded into Documents on the donut so any legacy rows from
  // before the allowlist shipped don't appear to vanish from the
  // user's total — they still count, they just visually merge with
  // the catch-all bucket. The API response still returns all four
  // numbers, so this fold is purely a rendering choice.
  const documentsValue =
    (breakdown.documents ?? 0) +
    (breakdown.media ?? 0) +
    (breakdown.archives ?? 0)
  // Display reconciliation — the headline total and each legend part
  // were previously rounded INDEPENDENTLY from raw bytes, so the
  // legend could read "216 MB + 104 MB = 320" beside a "319 MB used"
  // total (QA-reported). Round everything in the TOTAL's display unit
  // (same unit/precision rule as formatBytes), then absorb the ±1
  // rounding residual into the largest part so the parts always sum
  // to exactly the number the centre / subtitle shows.
  const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']
  const totalForUnit = Number(storage.usedBytes ?? 0)
  const unitIdx =
    totalForUnit > 0
      ? Math.min(
          BYTE_UNITS.length - 1,
          Math.floor(Math.log(totalForUnit) / Math.log(1024)),
        )
      : 0
  const unitDiv = Math.pow(1024, unitIdx)
  const unitDecimals =
    totalForUnit / unitDiv < 10 && unitIdx > 0 ? 1 : 0
  const roundInUnit = (b) => Number((b / unitDiv).toFixed(unitDecimals))
  const parts = [
    {
      label: 'Documents',
      value: documentsValue,
      color: DONUT_PALETTE[0], // shared brand palette — typically largest
    },
    {
      label: 'Images',
      value: breakdown.images ?? 0,
      color: DONUT_PALETTE[1],
    },
  ].map((p) => ({ ...p, roundedValue: roundInUnit(p.value) }))
  const roundingResidual = Number(
    (
      roundInUnit(totalForUnit) -
      parts.reduce((sum, p) => sum + p.roundedValue, 0)
    ).toFixed(unitDecimals),
  )
  if (roundingResidual !== 0) {
    const largest = parts.reduce((a, b) => (b.value > a.value ? b : a))
    largest.roundedValue = Number(
      (largest.roundedValue + roundingResidual).toFixed(unitDecimals),
    )
  }
  const segments = parts.map((p) => ({
    label: p.label,
    value: p.value,
    color: p.color,
    display: `${formatNumber(p.roundedValue)} ${BYTE_UNITS[unitIdx]}`,
  }))
  // DonutChart needs at least one non-zero segment to render; if
  // every bucket is 0 (fresh tenant, nothing uploaded) show an
  // empty-state message instead of a flat ring.
  if (segments.every((s) => !s.value)) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        No files uploaded yet.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Donut sized 132 × 18 — it now shares the "Storage & AI Usage"
       *  box with the AI meter, so it's a notch smaller than a full card's;
       *  the thinner ring keeps room for the "226 KB" centre value. */}
      <DonutChart
        segments={segments}
        centerLabel="Used"
        centerValue={formatBytes(storage.usedBytes)}
        size={132}
        thickness={18}
      />
      {/* Wrap-capable legend — beside the donut when the card is wide
       *  enough, below it when not (the container's flex-wrap), so
       *  "Documents" can never truncate; the min-w is wide enough for the
       *  longest label + its size, so a squeeze wraps instead of
       *  truncating. Pass raw byte counts so Legend's percentage calc
       *  works; format on display via the callback. */}
      <div className="flex-1 min-w-[10.5rem]">
        <Legend
          segments={segments}
          // Per-segment reconciled display (see the rounding block
          // above) — falls back to plain formatting for safety.
          formatValue={(bytes, pct, seg) =>
            seg?.display ?? formatBytes(bytes)
          }
        />
      </div>
    </div>
  )
}

/** AI Usage meter — third Analytics-row card.
 *
 *  Shows the tenant's current monthly AI consumption against the
 *  plan quota as a horizontal progress bar. Layout matches the
 *  Members + Storage donut cards' vertical rhythm so the row reads
 *  as a single block:
 *
 *    - Big stat (centred): percentage of quota used, or "Unlimited"
 *      pill when the plan has no monthly cap
 *    - Caption: "X of Y tokens used" ("X of Unlimited tokens used" when
 *      the plan has no cap — the monthly usage still shows)
 *    - Linear progress bar — green when comfortable, amber near
 *      the limit (≥80%), rose when over quota (≥100%)
 *    - State note: "Comfortable" / "Nearing limit" / "Over quota"
 *
 *  Reuses the same `/ai-usage` query key as `AiUsagePage` so the
 *  cache hydrates instantly when the operator drills into the
 *  full AI Usage report. With no data yet it distinguishes a plan that
 *  doesn't include AI usage, a failed fetch, and a load still in flight;
 *  once data exists it keeps rendering it, so a failed background refetch
 *  never blanks good numbers. */
function AiUsageMeter({ aiUsage, notInPlan, error }) {
  if (!aiUsage) {
    if (notInPlan) {
      return (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center">
          AI usage isn&apos;t included in your current plan.
        </p>
      )
    }
    if (error) {
      return (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          {error}
        </p>
      )
    }
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        Loading AI usage…
      </p>
    )
  }
  const unlimited = aiUsage.quota === null
  const used = aiUsage.currentMonthTotal ?? 0
  const quota = aiUsage.quota ?? 0
  const rate = aiUsage.quotaUsedRate ?? 0
  const overQuota = !unlimited && rate >= 100
  const nearQuota = !unlimited && rate >= 80 && !overQuota
  // Headline metric — percentage when capped, em-dash with an
  // "Unlimited" caption when not. Capped at 999% just in case the
  // backend ever returns an absurd value; UI shouldn't break.
  const headline = unlimited ? '∞' : `${Math.min(999, Math.round(rate))}%`
  const stateLabel = unlimited
    ? 'Unlimited plan'
    : overQuota
      ? 'Over quota'
      : nearQuota
        ? 'Nearing limit'
        : 'Comfortable'
  // Progress fill colour ladder. The "comfortable" / "unlimited"
  // branches paint with the brand navy (`--color-primary`) so the
  // progress bar reads as part of the same visual family as the
  // donuts sitting next to it on the Analytics row, instead of
  // jumping to a generic SaaS-green that didn't belong on a navy-
  // themed dashboard. Warning (amber) and over-quota (rose) keep
  // their stronger semantic tints so a tenant approaching or past
  // their cap still gets a real visual alarm.
  const barColor = unlimited
    ? 'bg-[color:var(--color-primary)]'
    : overQuota
      ? 'bg-[color:var(--color-danger)]'
      : nearQuota
        ? 'bg-[color:var(--color-warning)]'
        : 'bg-[color:var(--color-primary)]'
  const stateColor = unlimited
    ? 'text-[color:var(--color-primary)]'
    : overQuota
      ? 'text-[color:var(--color-danger)]'
      : nearQuota
        ? 'text-[color:var(--color-warning)]'
        : 'text-[color:var(--color-ink-muted)]'
  // Caption beneath the headline number — mirrors the AiUsagePage's
  // "X of Y tokens" treatment so the figure has the same context
  // wherever it appears. An unlimited plan still shows what it has used
  // this month ("X of Unlimited tokens used").
  const caption = `${formatNumber(used)} of ${
    unlimited ? 'Unlimited' : formatNumber(quota)
  } tokens used`
  // Concrete next-reset date, e.g. "Renews on 1 Jul". The earlier
  // "Resets on the 1st" wording read as cryptic on a card where the
  // 1st of which month was never specified — operators on the 28th
  // of a month had to do mental gymnastics to figure out whether
  // that meant "in 3 days" or "in 4 weeks". A real date removes
  // that ambiguity entirely.
  const now = new Date()
  const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  const nextResetLabel = formatDateTime(nextReset)
  return (
    <div className="flex flex-col gap-4 py-2">
      {/* Big headline percentage / unlimited glyph. Centred so the
       *  card has the same visual anchor as the centre-value of the
       *  donut on its left/right. */}
      <div className="text-center">
        <p className="font-display text-4xl tabular-nums text-[color:var(--color-ink-strong)] leading-none m-0">
          {headline}
        </p>
        <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)] tabular-nums">
          {caption}
        </p>
      </div>
      {/* Progress bar — 12 px tall, takes the full inner card width.
       *  Width clamps to 100% so the visual never spills past the
       *  track even when the tenant is over quota. */}
      <div className="h-3 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
        <div
          className={'h-full rounded-full transition-all ' + barColor}
          style={{
            width: unlimited ? '100%' : `${Math.min(100, Math.max(0, rate))}%`,
            opacity: unlimited ? 0.35 : 1,
          }}
        />
      </div>
      {/* State label + month-reset hint. State label reads as the
       *  primary signal on the left; the concrete renewal date sits
       *  as muted context on the right. */}
      <div className="flex items-baseline justify-between gap-2">
        <span className={'text-[13px] font-medium ' + stateColor}>
          {stateLabel}
        </span>
        {/* The used figure is a calendar-month total on every plan, so the
         *  reset date gives it its period even when there's no cap. */}
        <span className="text-[12px] text-[color:var(--color-ink-muted)]">
          Renews on {nextResetLabel}
        </span>
      </div>
    </div>
  )
}

/** Inline 24px-wide gold sparkline for a leaderboard row. Reuses
 *  `smoothPath` (the Catmull-Rom helper that drives the hero KPI
 *  sparkline) so the visual language stays consistent — same low-
 *  opacity area band, same curve smoothing.
 *
 *  Kept separate from the main `<Sparkline />` because the leaderboard
 *  variant needs a small fixed width / height and an inline (not
 *  absolute) layout so it sits between the points number and the
 *  trophy badge. */
function LeaderboardSparkline({ data, title }) {
  const { ref, hover, track, clear } = useChartHover()
  if (!data || data.length === 0) return null
  // 24px wide, 14 points — per the brief.
  const w = 60
  const h = 18
  const pad = 1
  const max = Math.max(1, ...data.map((d) => d.count))
  // Hide when every datapoint is zero; the caller renders a sized
  // placeholder so the row layout stays stable.
  if (max <= 0) return null
  const usableW = w - pad * 2
  const usableH = h - pad * 2
  const points = data.map((d, i) => [
    pad + (i * usableW) / Math.max(1, data.length - 1),
    pad + usableH - (d.count / max) * usableH,
  ])
  const linePath = smoothPath(points)
  const areaPath =
    linePath +
    ` L${points[points.length - 1][0]} ${h - pad}` +
    ` L${points[0][0]} ${h - pad} Z`
  // Gold tone — matches the trophy / leaderboard theme.
  const stroke = '#D97706'
  return (
    <span ref={ref} className="relative inline-block shrink-0 leading-none">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        width="60"
        height="18"
        className="shrink-0"
        role="img"
        aria-label={title}
      >
        <path d={areaPath} fill={stroke} stroke="none" opacity="0.18" />
        <path
          d={linePath}
          stroke={stroke}
          strokeWidth="1.25"
          fill="none"
          strokeLinecap="round"
        />
        {/* Invisible per-point hit circles — each surfaces a tooltip
         *  with that day's label + earned points count. The visible
         *  width matches the viewBox (60 × 18) so viewBox coordinates
         *  map 1:1 to wrapper pixels for the cursor-tracking tooltip. */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p[0]}
            cy={p[1]}
            r="6"
            fill="transparent"
            onMouseEnter={track({
              color: stroke,
              label: formatDate(data[i].day),
              value: formatNumber(data[i].count ?? 0),
            })}
            onMouseMove={track({
              color: stroke,
              label: formatDate(data[i].day),
              value: formatNumber(data[i].count ?? 0),
            })}
            onMouseLeave={clear}
          />
        ))}
      </svg>
      {hover ? <ChartTooltip {...hover} /> : null}
    </span>
  )
}

/** Body of the Top Performer card — just the content (avatar, name,
 *  points, leaderboard link). The AnalyticsCard wrapper around it
 *  provides the border/padding/title/subtitle. Replaced the older
 *  `TopPerformerTile` (KPI-strip variant) when the story moved into
 *  the Analytics row beside Quick Actions.
 *
 *  Phase 3 upgrade: each row now carries a delta chip (this period vs
 *  the previous 30 days) and an inline gold sparkline plotting daily
 *  point earnings across the last 14 days. The trophy badge moves
 *  out to its own column at the right so the row reads
 *  Rank · Avatar · Name+Dept · Points · Delta · Sparkline · Trophy. */
function TopPerformerBody({ leaderboard }) {
  if (!leaderboard?.length) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        Top performers will appear here once tasks are completed and
        points are awarded.
      </p>
    )
  }
  const rows = leaderboard.slice(0, 5)
  return (
    <div className="flex flex-col gap-3">
      <ul className="divide-y divide-[color:var(--color-rule)]">
        {rows.map((row, i) => {
          const rank = row.rank ?? i + 1
          const isTop = rank === 1
          const points = row.points ?? 0
          const lastPeriod = row.pointsLastPeriod ?? null
          const sparkline = row.pointsSparkline ?? []
          // Delta chip — null when we don't have a previous-period
          // total (older backend) so the chip simply doesn't render.
          let delta = null
          if (lastPeriod !== null) {
            const diff = points - lastPeriod
            if (diff > 0) delta = { kind: 'up', value: diff }
            else if (diff < 0) delta = { kind: 'down', value: Math.abs(diff) }
            else delta = { kind: 'flat', value: 0 }
          }
          // Sparkline tooltip — total earned + peak day (if any).
          const sparkTotal = sparkline.reduce(
            (s, p) => s + (p.count ?? 0),
            0,
          )
          const peak = sparkline.reduce(
            (best, p) =>
              p.count > (best?.count ?? -Infinity) ? p : best,
            null,
          )
          const peakLabel = peak && peak.count > 0
            ? `${formatDate(peak.day)}`
            : null
          const hoverTitle =
            sparkline.length > 0
              ? `Earned ${formatNumber(sparkTotal)} points in last 14 days${
                  peakLabel ? `, peak ${peak.count} on ${peakLabel}` : ''
                }`
              : null
          return (
            <li
              key={row.userId ?? row.fullName + i}
              title={hoverTitle ?? undefined}
              className="flex items-center gap-3 py-2.5 hover:bg-[color:var(--color-card-hover)] rounded"
            >
              {/* Rank pill — keeps the existing gold-tinted treatment
               *  for #1 so the row still reads as "leaderboard". */}
              <span
                className={
                  'inline-flex items-center justify-center h-6 min-w-[28px] px-1.5 rounded-full text-[13px] font-display tabular-nums border shrink-0 ' +
                  (isTop
                    ? 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20'
                    : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]')
                }
              >
                #{rank}
              </span>
              <Avatar
                name={row.fullName}
                avatarUrl={row.avatarUrl ?? null}
                size="sm"
              />
              {/* Name + dept stack — primary line is the name, the
               *  department drops to a muted second line so the
               *  primary signal (who) reads first. */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-normal text-[color:var(--color-ink-strong)] truncate m-0">
                  {row.fullName}
                </p>
                <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate m-0">
                  {row.departmentName ?? '—'}
                </p>
              </div>
              {/* Points (primary) — tabular-nums so the column edge
               *  stays vertically aligned regardless of digit count. */}
              <span className="font-display text-sm text-[color:var(--color-ink-strong)] tabular-nums shrink-0 min-w-[44px] text-right">
                {formatNumber(points)}
              </span>
              {/* Delta chip — secondary signal. Emerald up / rose
               *  down / muted dash for no change. Hidden when the
               *  backend doesn't surface `pointsLastPeriod` (e.g.
               *  pre-Phase-3 cached response). */}
              {delta ? (
                <span
                  className={
                    'inline-flex items-center gap-0.5 text-[13px] font-medium tabular-nums shrink-0 ' +
                    (delta.kind === 'up'
                      ? 'text-[color:var(--color-success)]'
                      : delta.kind === 'down'
                        ? 'text-[color:var(--color-danger)]'
                        : 'text-[color:var(--color-ink-muted)]')
                  }
                  aria-label={
                    delta.kind === 'up'
                      ? `Up ${delta.value} points vs previous period`
                      : delta.kind === 'down'
                        ? `Down ${delta.value} points vs previous period`
                        : 'No change vs previous period'
                  }
                >
                  {delta.kind === 'up' ? (
                    <TrendingUp size={11} aria-hidden="true" />
                  ) : delta.kind === 'down' ? (
                    <TrendingDown size={11} aria-hidden="true" />
                  ) : (
                    <Minus size={11} aria-hidden="true" />
                  )}
                  {delta.kind === 'up'
                    ? `+${delta.value}`
                    : delta.kind === 'down'
                      ? `-${delta.value}`
                      : '—'}
                </span>
              ) : null}
              {/* Mini sparkline — gold to match the trophy. Always
               *  occupies the same 60-px slot so the row geometry
               *  doesn't jump between rows that have data and
               *  flat-line rows. `LeaderboardSparkline` returns
               *  null when every datapoint is zero; the wrapping
               *  span provides the placeholder width. */}
              <span className="inline-block w-[60px] h-[18px] shrink-0">
                <LeaderboardSparkline
                  data={sparkline}
                  title={`${row.fullName} — last 14 days of points`}
                />
              </span>
              {/* Trophy badge — only the top row gets one, matching
               *  the existing decoration. Sits at the row's tail
               *  per the new layout. */}
              <span className="inline-flex w-4 justify-center shrink-0">
                {isTop ? (
                  <Trophy
                    size={13}
                    className="text-[#92400E]"
                    aria-hidden="true"
                  />
                ) : null}
              </span>
            </li>
          )
        })}
      </ul>
      <Link
        to="/leaderboard"
        className="inline-flex items-center gap-1 font-normal text-[color:var(--color-accent-strong)] hover:underline"
      >
        View full leaderboard
        <ArrowRight size={12} />
      </Link>
    </div>
  )
}

/** Quick Actions card body — three sequential onboarding steps that
 *  mirror the prerequisites-first ordering on the Company Admin
 *  sidebar (Workspaces → Departments → Members). The Dashboard's
 *  Quick Actions thus reads as a day-one checklist that an admin
 *  can walk through end-to-end without leaving the page.
 *
 *  Each row is a Link to the destination page; the create flow
 *  (modal / form) lives there. The numbered badge on the left
 *  reinforces "do these in order"; the chevron + label do the
 *  rest. Vertically stacked so the sequence reads top-to-bottom. */
function QuickActions() {
  // Icons were previously rendered to the left of the label; removed
  // per the dashboard cleanup pass — the numbered badges (1/2/3/4)
  // already convey the sequence and the label is descriptive enough.
  // Leaves more horizontal room for longer labels at narrow widths.
  const steps = [
    { n: 1, label: 'Add Department', to: '/departments' },
    { n: 2, label: 'Add Work Roles', to: '/work-roles' },
    { n: 3, label: 'Add Members', to: '/members' },
    { n: 4, label: 'Configure Schedule', to: '/reminders-schedule' },
  ]
  return (
    <ol className="flex flex-col gap-2">
      {steps.map((s) => (
        <li key={s.to}>
          <Link
            to={s.to}
            className="group flex items-center gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2.5 hover:border-[color:var(--color-accent-border)] hover:bg-[color:var(--color-accent-bg)] transition-colors cursor-pointer"
          >
            {/* Numbered step badge — the only visual marker now that
             *  the per-step icon was removed. Sits flush-left so the
             *  sequence reads cleanly down the column. */}
            <span className="h-6 w-6 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] flex items-center justify-center shrink-0 text-[13px] font-medium tabular-nums">
              {s.n}
            </span>
            <span className="text-sm font-normal text-[color:var(--color-ink-strong)] flex-1 truncate">
              {s.label}
            </span>
            <ArrowRight
              size={12}
              className="text-[color:var(--color-ink-muted)] shrink-0 group-hover:text-[color:var(--color-accent-strong)] group-hover:translate-x-0.5 transition-transform"
            />
          </Link>
        </li>
      ))}
    </ol>
  )
}

/** Per-category visual identity for the Recent Activity feed — the
 *  label, icon, and a colour-coded badge class. Each colour family
 *  matches the TONE_CLASSES palette used across the dashboard so the
 *  feed reads as the same visual language: Reports = sky, Tasks =
 *  violet, Comments = pink, Files = amber. Drives both the small
 *  type-dot overlaid on each avatar AND the inline category badge,
 *  so the activity type is identifiable at a glance on every row. */
const CATEGORY_META = {
  task: {
    label: 'Task',
    icon: LayoutList,
    badge: 'bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
  },
  report: {
    label: 'Report',
    icon: ClipboardList,
    badge: 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
  },
  comment: {
    label: 'Comment',
    icon: MessageSquare,
    badge: 'bg-[#FCE7F3] text-[#DB2777] border-[#FBCFE8] dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-400/20',
  },
  file: {
    label: 'File',
    icon: Folder,
    badge: 'bg-[#FFEDD5] text-[#EA580C] border-[#FED7AA] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
  },
  badge: {
    label: 'Badge',
    icon: Award,
    badge: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
  },
}

/** Category-filterable Recent Activity feed. Five pill chips above the
 *  list (All / Tasks / Reports / Comments / Files) filter the items
 *  by the server-side `category` field. Counts in each chip's badge
 *  are precomputed in a single pass below — O(n) instead of running
 *  a `.filter(...).length` per chip on every render, which a `useMemo`
 *  would otherwise hide behind a re-render cost.
 *
 *  The filter is purely client-side; the backend already ships the
 *  full activity list (capped at 12) so no extra fetch is needed
 *  when switching chips. */
function RecentActivityCard({ activity }) {
  const [filter, setFilter] = useState('all')

  // Single pass over `activity` to precompute the chip badge counts.
  // Items without a category (older backends) only show under "All",
  // so the per-bucket counts don't include them. Counts are stable
  // for the lifetime of the prop — recomputed only when `activity`
  // changes — but since the component is small we avoid useMemo and
  // just compute on each render.
  const counts = { all: activity.length, task: 0, report: 0, comment: 0, file: 0, badge: 0 }
  for (const ev of activity) {
    if (ev.category && ev.category in counts) {
      counts[ev.category] += 1
    }
  }

  const chips = [
    { key: 'all', label: 'All', icon: null },
    { key: 'task', label: 'Tasks', icon: LayoutList },
    { key: 'report', label: 'Reports', icon: ClipboardList },
    { key: 'comment', label: 'Comments', icon: MessageSquare },
    { key: 'file', label: 'Files', icon: Folder },
    { key: 'badge', label: 'Badges', icon: Award },
  ]

  // Filter the list based on the current chip selection. `all` is a
  // no-op pass-through; the other four chips compare against the
  // server-stamped `category`.
  const filtered = filter === 'all'
    ? activity
    : activity.filter((ev) => ev.category === filter)

  return (
    // `flex flex-col` + the `flex-1` list below let the card fill the
    // grid-stretched height and the activity list scroll inside it, so
    // this card and the Report Cadence card beside it stay the same
    // height with the scrollbar carrying any overflow.
    <div className="lg:col-span-2 flex flex-col rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-12 w-12 rounded-xl bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20 flex items-center justify-center">
          <Activity size={20} />
        </span>
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          Recent Activity
        </h2>
      </div>

      {/* Filter chip row — sits directly under the title. Same pill
       *  styling as the filter chips used on Task Monitor / Team
       *  Tasks: canvas/ink-muted by default, accent-bg + accent-
       *  strong text + accent-border when selected. The All chip
       *  is always present per the brief. */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {chips.map((chip) => {
          const Icon = chip.icon
          const isActive = filter === chip.key
          const count = counts[chip.key] ?? 0
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setFilter(chip.key)}
              className={
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] font-normal transition-colors ' +
                (isActive
                  ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
                  : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)] hover:text-[color:var(--color-ink-strong)]')
              }
              aria-pressed={isActive}
            >
              {Icon ? <Icon size={12} aria-hidden="true" /> : null}
              <span>{chip.label}</span>
              <span
                className={
                  'inline-flex items-center justify-center rounded-full text-[11px] tabular-nums px-1 min-w-[18px] ' +
                  (isActive
                    ? 'bg-[color:var(--color-accent-strong)] text-white'
                    : 'bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]')
                }
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* List body. Two empty states:
       *   - the whole feed is empty → original "Quiet for now" copy.
       *   - the active filter has no matches → category-specific copy
       *     per the brief. */}
      {activity.length === 0 ? (
        <div className="py-8 text-center">
          <Sparkles
            size={20}
            className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
          />
          <p className="text-sm font-normal text-[color:var(--color-ink-strong)]">
            Quiet for now
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Task updates, report submissions, and badge awards land
            here as they happen.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            No {filter} activity in the last 14 days.
          </p>
        </div>
      ) : (
        // Fill the remaining card height and scroll for overflow. The
        // scrollbar appears once the entries exceed the visible area, so
        // every log stays reachable while the card height matches the
        // Report Cadence card. `overflow-x-hidden` pairs with
        // `overflow-y-auto` so the rows' -mx-2 hover bleed doesn't
        // trigger a stray horizontal scrollbar. `min-h-[12rem]` keeps a
        // sensible floor when there are only a couple of entries.
        // `tabIndex=0` + a group role: the list scrolls but holds no focusable
        // children, so without this a keyboard-only user cannot scroll it and
        // the entries past the fold are unreachable (WCAG 2.1.1).
        // No `role` override here: a <ul> already exposes role="list", and
        // replacing it with "group" orphaned all 12 <li> children (axe's
        // `listitem` rule). aria-label names the region without touching it.
        <ul
          tabIndex={0}
          aria-label="Recent activity"
          className="space-y-0.5 flex-1 min-h-0 max-h-[13rem] overflow-y-auto overflow-x-hidden pr-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-accent)]"
        >
          {filtered.map((ev, i) => {
            const KindIcon = iconForKind(ev.kind)
            const tone = toneForKind(ev.kind)
            const cat = ev.category ? CATEGORY_META[ev.category] : null
            const CatIcon = cat?.icon ?? KindIcon
            // Lead line is the actor's name when we have one; otherwise
            // (system events like badge awards) the description is
            // promoted to the top line so the row never shows a blank
            // name slot.
            const hasActor = Boolean(ev.actorName)
            const body = ev.description ?? ev.title
            const Inner = (
              <div className="flex items-start justify-between gap-3 rounded-lg -mx-2 px-2 py-2.5 transition-colors hover:bg-[color:var(--color-card-hover)]">
                {/* Left — avatar + actor/action text. Mirrors the
                 *  Super Admin "Recent Platform Events" feed: who on the
                 *  first line, a category chip + the action on the
                 *  second. */}
                <div className="min-w-0 flex items-start gap-3">
                  {/* Avatar (photo or initials) for a real actor; a
                   *  kind-tinted icon orb for system events. A small
                   *  category-coloured type dot overlays the corner so
                   *  the activity type reads at a glance. */}
                  <div className="relative shrink-0">
                    {hasActor ? (
                      <Avatar
                        name={ev.actorName}
                        avatarUrl={ev.actorAvatarUrl ?? null}
                        size="md"
                        colorByName
                      />
                    ) : (
                      <span
                        className={
                          'h-9 w-9 rounded-full flex items-center justify-center ' +
                          TONE_CLASSES[tone].tile
                        }
                      >
                        <KindIcon size={16} />
                      </span>
                    )}
                    {cat ? (
                      <span
                        aria-hidden="true"
                        className={
                          'absolute -bottom-1 -right-1 h-[18px] w-[18px] rounded-full flex items-center justify-center border ring-2 ring-[color:var(--color-card)] ' +
                          cat.badge
                        }
                      >
                        <CatIcon size={10} />
                      </span>
                    ) : null}
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate m-0">
                      {hasActor ? ev.actorName : body}
                    </p>
                    {/* Category chip inline + the action text. */}
                    <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)] truncate m-0 flex items-center gap-1.5">
                      {cat ? (
                        <span
                          className={
                            'shrink-0 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ' +
                            cat.badge
                          }
                        >
                          <CatIcon size={9} aria-hidden="true" />
                          {cat.label}
                        </span>
                      ) : null}
                      {hasActor ? <span className="truncate">{body}</span> : null}
                    </p>
                  </div>
                </div>

                {/* Right — absolute date + time (the requested addition,
                 *  matching the platform-events feed's right-aligned
                 *  timestamp but with the time below the date). */}
                <div className="shrink-0 text-right">
                  <p className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums m-0">
                    {formatDateTime(ev.at ?? null)}
                  </p>
                </div>
              </div>
            )
            return (
              <li key={`${ev.kind}-${ev.at ?? i}-${i}`}>
                {ev.link ? (
                  <Link to={ev.link} className="block">
                    {Inner}
                  </Link>
                ) : (
                  Inner
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// `PendingApprovalsCard` and `SopUsageCard` were removed in the
// May-2026 dashboard restructure — pending approvals now surface inside
// the Action-Needed row directly (no card wrapper), and SOP metrics
// merged into the AI Insights strip. The component bodies sat unused
// for several sweeps and were pruned to keep the file scannable.

function DashboardBody({
  data,
  storage,
  storageError,
  storageErrorMessage,
  aiUsage,
  aiUsageNotInPlan,
  aiUsageError,
}) {
  const { members, tasks, reports, leaderboard, schedule } = data
  const openTasks = tasks.openCount ?? 0

  // KPI headline numbers — each tile renders a raw current count.
  // The "+100% vs last week" / "No change vs last week" delta chips
  // that used to sit below these values were removed because they
  // computed misleading figures against a zero baseline on fresh
  // tenants (every change reads as "+100%" or "No change"). All the
  // upstream delta-computation blocks (`completedDelta`,
  // `openTasksDelta`, `activeDelta`, `overdueDelta`) along with the
  // `*Sparkline` series they relied on were removed here.
  const completedTasks = tasks.completedCount ?? 0
  const overdueTasks = tasks.overdueCount ?? 0
  // Non-repeat / Repeat Task Completion pies (Analytics row). An older API
  // without `repeatSplit` falls back to every task as non-repeat, so the
  // row still renders.
  const repeatSplit = tasks.repeatSplit ?? {
    nonRepeat: { total: tasks.total ?? 0, ...taskHealthPartition(tasks) },
    repeat: { total: 0, completed: 0, overdue: 0, onTrack: 0 },
  }

  const atRiskTasks = data.atRiskTasks ?? []

  // Setup-checklist derivations removed 2026-05-22 per user request.
  // The backend `data.setupChecklist` field still ships but the
  // frontend no longer reads it.

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────
       *  Hero KPI strip — four premium tiles, each with optional
       *  sparkline + week-over-week delta chip. Open Tasks · Completed
       *  This Week · Overdue Tasks · Active Members.
       *
       *  Slot 3 was previously "Hours Logged · This Week" but was
       *  swapped out 2026-05-27: that tile read 0m on every tenant
       *  that hadn't yet adopted the task timer, leaving the hero
       *  strip with a permanently dead pixel. Overdue Tasks is
       *  informative from day one and pairs with the At-Risk Tasks
       *  card directly below.
       *  ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiHeroTile
          label="To Do"
          value={openTasks}
          icon={ClipboardList}
          tone="violet"
          to="/task-monitor"
        />
        <KpiHeroTile
          label="Completed Tasks"
          value={completedTasks}
          icon={CheckCircle2}
          tone="emerald"
          // All-time CANONICAL completed count — Boolean(completedAt),
          // matching Analytics / Personal / Team (subtasks included).
          // Deep-links to the Task Monitor's Completed filter, which is
          // category-based (statusCategory=done); when cancelled-but-stamped
          // tasks exist this tile can read higher than that filtered list.
          to="/task-monitor?filter=complete"
        />
        <KpiHeroTile
          label="Overdue Tasks"
          value={overdueTasks}
          icon={AlertCircle}
          tone="rose"
          to="/task-monitor?filter=overdue"
        />
        {/* Active Members = the workforce, EXCLUDING admins — consistent with
         *  the Work Role donut / Report Compliance widgets below (work roles /
         *  daily reports don't apply to admins, so they aren't counted here
         *  either). */}
        <KpiHeroTile
          // Lifetime count of active member accounts — NOT a 14-day activity
          // window (the old "· 14 Days" suffix misdescribed it; see the
          // admin.service note). Matches the Sub Admin "Active Members" tile.
          label="Active Members"
          value={members.activeUsersCount}
          icon={UserCheck}
          tone="sky"
          to="/members"
        />
      </div>

      {/* Setup Checklist card was removed 2026-05-22 — the card +
       *  success badge + 7 row helpers (`SetupChecklistCard`,
       *  `SetupChecklistRow`, `SetupCompleteBadge`) were deleted in
       *  the same pass. Dashboard now flows hero KPIs → Action Needed
       *  directly. */}

      {/* ─────────────────────────────────────────────────────────────
       *  ACTION NEEDED — Quick Actions paired with At-Risk Tasks.
       *  Lives above the analytics row so the admin sees "what to do
       *  next" (Quick Actions setup checklist) and "what's burning"
       *  (At-Risk triage) before the charts. 3-column grid: actions
       *  take 1 column, the triage list spans the remaining 2 so the
       *  list has room to breathe while the action column stays the
       *  same width as a single AnalyticsCard.
       *  ──────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Action Needed"
        hint="What to do next, and what's already overdue"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Actions — sequential onboarding steps. Left column so
         *  the admin's eye lands on "do these in order" before scanning
         *  the at-risk list to the right. */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-12 w-12 rounded-xl bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20 flex items-center justify-center">
              <Lightbulb size={20} />
            </span>
            <div>
              <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                Quick Actions
              </h2>
              <p className="text-[13px] text-[color:var(--color-ink-muted)]">
                Day-One Setup Checklist
              </p>
            </div>
          </div>
          <QuickActions />
        </div>
        {/* At-Risk Tasks — spans the remaining 2 columns. `flex flex-col`
         *  so the list below can flex/scroll within the card height that
         *  the (shorter) Quick Actions card sets, keeping both cards the
         *  same height instead of this one growing with its row count. */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <span className="h-12 w-12 rounded-xl bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20 flex items-center justify-center">
                <AlertCircle size={20} />
              </span>
              <div>
                <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                  At-Risk Tasks
                </h2>
                <p className="text-[13px] text-[color:var(--color-ink-muted)]">
                  Overdue, due today, or stale. Triage these first.
                </p>
              </div>
            </div>
            {/* Escape hatch to the full Task Monitor — the widget's
             *  feed is server-capped per bucket, so even the uncapped
             *  scroll list below can't guarantee every at-risk task
             *  appears on busy tenants. */}
            <Link
              to="/task-monitor"
              className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
            >
              View All
              <ArrowRight size={12} />
            </Link>
          </div>
          {/* Every row the API returns renders in the list — it already
           *  scrolls (max-h + overflow-y), so the old `.slice(0, 8)`
           *  just silently hid the tail (QA: 11 at-risk tasks, only 8
           *  shown, no way to reach the rest). */}
          {atRiskTasks.length === 0 ? (
            <div className="py-8 text-center">
              <CheckCircle2
                size={28}
                className="mx-auto mb-2 text-[color:var(--color-success)]"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                All clear — no overdue or stale tasks right now.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--color-rule)] mt-3 flex-1 min-h-0 max-h-[20rem] overflow-y-auto overflow-x-hidden pr-1">
              {atRiskTasks.map((t) => (
                <li key={t.id}>
                  <AtRiskRow task={t} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
       *  ANALYTICS — two breakdown donuts in one row. Top Performers
       *  used to live here as the third (right-hand) card, but was
       *  promoted to its own prominent row below — it now sits where
       *  "Tasks Completed by Department" used to live. With only two
       *  cards left this row drops to a 2-column grid; the donuts
       *  stretch to fill the wider canvas comfortably.
       *  ──────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Analytics"
        hint="Live breakdowns across tasks, storage, and AI usage"
      />
      {/* Three-card Analytics row: Non-repeat Task Completion · Repeat Task
       *  Completion · Storage & AI Usage (one box). 3-up on lg, stacked below
       *  that. The two pies split every task by whether it comes from a
       *  repeat rule (server: utils/agg-expr isRepeatTaskExpr — the
       *  Analytics page's definition); together they add up to the To Do /
       *  Completed / Overdue tiles' population. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <AnalyticsCard
          title="Non-repeat Task Completion"
          subtitle={`${formatNumber(repeatSplit.nonRepeat.total ?? 0)} total`}
        >
          <TaskCompletionPie part={repeatSplit.nonRepeat} empty="No non-repeat tasks yet." />
        </AnalyticsCard>
        <AnalyticsCard
          title="Repeat Task Completion"
          subtitle={`${formatNumber(repeatSplit.repeat.total ?? 0)} total`}
        >
          <TaskCompletionPie part={repeatSplit.repeat} empty="No repeat tasks yet." />
        </AnalyticsCard>
        {/* Storage by File Type + AI Usage share one box (on request). */}
        <AnalyticsCard title="Storage & AI Usage">
          <div className="flex flex-col gap-5">
            <section aria-label="Storage by File Type">
              <SubCardHeading
                title="Storage by File Type"
                meta={storage ? formatBytes(storage.usedBytes) + ' used' : '—'}
              />
              <StorageDonut
                storage={storage}
                error={storageError}
                errorMessage={storageErrorMessage}
              />
            </section>
            <hr className="m-0 border-0 border-t border-[color:var(--color-rule)]" />
            <section aria-label="AI Usage">
              <SubCardHeading
                title="AI Usage"
                meta={
                  aiUsage
                    ? aiUsage.quota === null
                      ? 'Unlimited'
                      : `${Math.round(aiUsage.quotaUsedRate ?? 0)}% used`
                    : aiUsageNotInPlan
                      ? 'Not in plan'
                      : '—'
                }
              />
              <AiUsageMeter
                aiUsage={aiUsage}
                notInPlan={aiUsageNotInPlan}
                error={aiUsageError}
              />
            </section>
          </div>
        </AnalyticsCard>
      </div>

      {/* ─── Activity Trends ─────────────────────────────────────────
       *  Tasks by Department (tasks completed per department over 30 days,
       *  productivityByDepartment — a ranked bar chart in the shared brand
       *  palette) + Daily Active Users over the last 14 days. Both read
       *  arrays already on the overview payload. The department card took
       *  the old "Tasks by Status" slot and replaced the separate
       *  "Workload by Department" section (Company Admin only — the Sub
       *  Admin dashboard still shows Tasks by Status). */}
      <SectionHeader
        title="Activity Trends"
        hint="Tasks completed by department and daily active users"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <AnalyticsCard
          title="Tasks by Department · 30 Days"
          subtitle={`${formatNumber(
            (data.productivityByDepartment ?? []).reduce((a, d) => a + (d.tasksCompleted ?? 0), 0),
            )} completed`}
        >
          {/* Slightly thinner bars than the default (on request, 2026-10-07). */}
          <RankedBarChart
            size="slim"
            rows={(data.productivityByDepartment ?? [])
              .slice()
              .sort((a, b) => (b.tasksCompleted ?? 0) - (a.tasksCompleted ?? 0))
              .map((d) => ({
                label: d.departmentName,
                value: d.tasksCompleted ?? 0,
              }))}
            formatValue={(v) =>
              `${formatNumber(v)} ${v === 1 ? 'task' : 'tasks'}`
            }
            emptyMessage="No department task data yet"
          />
        </AnalyticsCard>
        <AnalyticsCard title="Daily Active Users" subtitle="Last 14 days">
          <TrendChart
            labels={(members.activeUsersSparkline ?? []).map((p) => p.day)}
            series={[
              {
                name: 'Active users',
                color: 'var(--color-accent-strong)',
                data: (members.activeUsersSparkline ?? []).map((p) => p.count),
              },
            ]}
            labelFormat={formatDate}
            emptyMessage="No active users in the last 14 days"
          />
        </AnalyticsCard>
      </div>

      {/* ─────────────────────────────────────────────────────────────
       *  TOP PERFORMERS — leaderboard on the left (replacing the old
       *  "Tasks Completed by Department" bar chart, which was retired
       *  per design request), Points by Department on the right. The
       *  Top Performers card uses the shared `TopPerformerBody` so it
       *  reads identically to the old small Analytics-row variant —
       *  just with more vertical room because it's no longer
       *  squeezed alongside two donuts.
       *  ──────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Top Performers"
        hint="Most-rewarded members and how points distribute across departments"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="h-12 w-12 rounded-xl bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20 flex items-center justify-center">
                <Trophy size={20} />
              </span>
              <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                Top Performers
              </h2>
            </div>
            <span className="text-[13px] text-[color:var(--color-ink-muted)] shrink-0">
              Last 30 days
            </span>
          </div>
          <TopPerformerBody leaderboard={leaderboard} />
        </div>
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-12 w-12 rounded-xl bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20 flex items-center justify-center">
              <Award size={20} />
            </span>
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
              Points by Department · 30 Days
            </h2>
          </div>
          <BarRows
            rows={data.pointsByDepartment.slice(0, 6).map((d) => ({
              name: d.departmentName,
              value: d.points,
            }))}
            formatter={(n) => `${formatNumber(n)} pts`}
            emptyLabel="No points awarded yet."
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
       *  ACTIVITY — feed of recent events on the left, current cadence
       *  schedule on the right (so admins know what reminders are
       *  firing).
       *  ──────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Activity"
        hint="What's happened lately and the cadence reminders driving it"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RecentActivityCard activity={data.activity ?? []} />
        {/* Both cards stretch to the same height (grid default). Recent
         *  Activity carries any overflow in its own scroll area, so this
         *  card simply matches its height. */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="h-12 w-12 rounded-xl bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] flex items-center justify-center">
                <CalendarClock size={20} />
              </span>
              <div>
                <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                  Report Cadence
                </h2>
                <p className="text-[13px] text-[color:var(--color-ink-muted)]">
                  When teammates are reminded
                </p>
              </div>
            </div>
            <Link
              to="/reminders-schedule"
              className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
            >
              Edit
              <ArrowRight size={12} />
            </Link>
          </div>
          {schedule ? (
            <div className="space-y-2">
              <CadenceRow
                label="Daily"
                enabled={schedule.dailyEnabled}
                detail={`Reminder at ${schedule.dailyReminderAt}`}
              />
              <CadenceRow
                label="Weekly"
                enabled={schedule.weeklyEnabled}
                detail={`${WEEKDAYS[schedule.weeklyDayOfWeek]} · ${schedule.weeklyReminderAt}`}
              />
              <CadenceRow
                label="Monthly"
                enabled={schedule.monthlyEnabled}
                detail={`Day ${schedule.monthlyDayOfMonth} · ${schedule.monthlyReminderAt}`}
              />
            </div>
          ) : (
            <p className="text-sm text-[color:var(--color-ink-muted)]">
              Cadence not yet configured —{' '}
              <Link
                to="/reminders-schedule"
                className="text-[color:var(--color-accent-strong)] hover:underline"
              >
                set it up
              </Link>
              .
            </p>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
       *  REPORT COMPLIANCE — three rings (Daily / Weekly / Monthly).
       *  Last section on the page (moved to the end on request), after
       *  the Activity feed + Report Cadence.
       *  ──────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Report Compliance"
        hint="Workforce members who submitted on time — admins don't file reports"
        action={
          <Link
            to="/work-reports"
            className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
          >
            All submissions
            <ArrowRight size={12} />
          </Link>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ComplianceRing
          label="Daily — today"
          rate={reports.dailyToday.complianceRate}
          submitted={reports.dailyToday.submitted}
          eligible={reports.dailyToday.eligible}
          tone={
            reports.dailyToday.complianceRate >= 80
              ? 'emerald'
              : reports.dailyToday.complianceRate >= 50
                ? 'amber'
                : 'rose'
          }
        />
        <ComplianceRing
          label="Weekly — this week"
          rate={reports.weeklyThisWeek.complianceRate}
          submitted={reports.weeklyThisWeek.submitted}
          eligible={reports.weeklyThisWeek.eligible}
          tone={
            reports.weeklyThisWeek.complianceRate >= 80
              ? 'emerald'
              : reports.weeklyThisWeek.complianceRate >= 50
                ? 'amber'
                : 'rose'
          }
        />
        <ComplianceRing
          label="Monthly — this month"
          rate={reports.monthlyThisMonth.complianceRate}
          submitted={reports.monthlyThisMonth.submitted}
          eligible={reports.monthlyThisMonth.eligible}
          tone={
            reports.monthlyThisMonth.complianceRate >= 80
              ? 'emerald'
              : reports.monthlyThisMonth.complianceRate >= 50
                ? 'amber'
                : 'rose'
          }
        />
      </div>

    </>
  )
}

function CadenceRow({
  label,
  enabled,
  detail,
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3 flex items-center justify-between">
      <div>
        <h3 className="text-sm uppercase tracking-wider font-medium text-[color:var(--color-ink-muted)] m-0">
          {label}
        </h3>
        <p className="mt-0.5 text-sm text-[color:var(--color-ink-strong)]">
          {detail}
        </p>
      </div>
      <span
        className={
          'text-[13px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded shrink-0 ' +
          (enabled
            ? 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]'
            : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]')
        }
      >
        {enabled ? 'On' : 'Off'}
      </span>
    </div>
  )
}

function iconForKind(kind) {
  if (kind.startsWith('report.')) return FileText
  if (kind.startsWith('badge.')) return Award
  if (kind === 'task.completed') return CheckCircle2
  if (kind.startsWith('task.')) return LayoutList
  if (kind.startsWith('comment.')) return MessageSquare
  return Activity
}

function toneForKind(kind) {
  if (kind.startsWith('report.')) return 'sky'
  if (kind.startsWith('badge.')) return 'gold'
  if (kind.startsWith('comment.')) return 'pink'
  if (kind.startsWith('task.')) return 'violet'
  return 'slate'
}
